import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { getFieldValue, getReceiptFromFields } from "./sessionUtils";

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

export const CENTRAL_TZ = "America/Chicago";
export const CENTRAL_DATETIME_FORMAT = "MM/DD/YYYY, hh:mm:ss A";

export const FLAG_OPTIONS = [
  "Green",
  "Yellow",
  "Orange",
  "Red",
  "DNC",
  "DNS",
  "MOD",
  "RECON",
];

export function getFlagOptions(organizationId) {
  if (organizationId === "crane") {
    return FLAG_OPTIONS.filter((option) => option !== "Orange");
  }

  return FLAG_OPTIONS;
}

export const EMPTY_POD_NOTES = {
  notes: "",
  flag: "",
  receipt: "",
  exam: "",
  idCheck: false,
  roomCheck: false,
  personCheck: false,
  score: "",
  instructor: "",
  examCode: "",
  confirmSubmission: false,
  secondaryProctorNotes: "",
  secondaryProctorLink: "",
  secondaryProctorName: "",
  startTime: "",
  endTime: "",
  student_id: "",
};

function isMachineReadableDateTime(value) {
  const str = String(value).trim();
  if (!str) return false;
  if (/^\d{13}$/.test(str)) return true;
  if (/^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}|$)/.test(str)) return true;
  return str.endsWith("Z");
}

function toStoredCentralDateTimeString(value) {
  if (value == null) return "";

  const str = String(value).trim();
  if (!str) return "";

  const strictCentralTime = parseCentralDateTime(str);
  if (strictCentralTime) {
    return strictCentralTime.format(CENTRAL_DATETIME_FORMAT);
  }

  if (!isMachineReadableDateTime(str)) return str;

  const parsed = dayjs(str);
  if (!parsed.isValid()) return str;

  return parsed.tz(CENTRAL_TZ).format(CENTRAL_DATETIME_FORMAT);
}

export function parseCentralDateTime(value) {
  const str = String(value ?? "").trim();
  if (!str) return null;

  // Strict wall-clock validation before timezone conversion.
  if (!dayjs(str, CENTRAL_DATETIME_FORMAT, true).isValid()) return null;

  let parsed;
  try {
    parsed = dayjs.tz(str, CENTRAL_DATETIME_FORMAT, CENTRAL_TZ);
  } catch {
    return null;
  }

  if (!parsed.isValid()) return null;

  // Reject lenient overflow/normalization (e.g. 02/31 → 03/03).
  if (parsed.format(CENTRAL_DATETIME_FORMAT) !== str) return null;

  return parsed;
}

export function isValidCentralDateTimeFormat(value) {
  return parseCentralDateTime(value) !== null;
}

export function isCentralEndTimeAfterStartTime(startTime, endTime) {
  const parsedStartTime = parseCentralDateTime(startTime);
  const parsedEndTime = parseCentralDateTime(endTime);

  if (!parsedStartTime || !parsedEndTime) return false;

  return parsedEndTime.isAfter(parsedStartTime);
}

function coerceBoolean(value) {
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return Boolean(value);
}

function resolveReceipt(parsedReceipt, fields) {
  return parsedReceipt || getReceiptFromFields(fields ?? []);
}

function defaultFieldValues(fields) {
  return {
    receipt: getReceiptFromFields(fields ?? []),
    exam: getFieldValue(fields ?? [], "exam"),
    instructor: getFieldValue(fields ?? [], "instructor"),
    examCode: getFieldValue(fields ?? [], "examCode"),
  };
}

export function parsePodSessionNotes(notesString, fields) {
  const defaults = defaultFieldValues(fields);

  if (!notesString) {
    return { ...EMPTY_POD_NOTES, ...defaults };
  }

  try {
    const parsed = JSON.parse(notesString);

    if (typeof parsed !== "object" || parsed === null) {
      return { ...EMPTY_POD_NOTES, notes: String(notesString), ...defaults };
    }

    return {
      ...EMPTY_POD_NOTES,
      ...defaults,
      ...parsed,
      idCheck: coerceBoolean(parsed.idCheck),
      roomCheck: coerceBoolean(parsed.roomCheck),
      personCheck: coerceBoolean(parsed.personCheck),
      confirmSubmission: coerceBoolean(parsed.confirmSubmission),
      receipt: resolveReceipt(parsed.receipt, fields),
      exam: parsed.exam || defaults.exam,
      instructor: parsed.instructor || defaults.instructor,
      examCode: parsed.examCode || defaults.examCode,
      startTime: toStoredCentralDateTimeString(parsed.startTime),
      endTime: toStoredCentralDateTimeString(parsed.endTime),
    };
  } catch {
    return {
      ...EMPTY_POD_NOTES,
      notes: notesString,
      ...defaults,
    };
  }
}

export function serializePodSessionNotes(notesState) {
  return JSON.stringify({
    ...notesState,
    startTime: toStoredCentralDateTimeString(notesState.startTime),
    endTime: toStoredCentralDateTimeString(notesState.endTime),
  });
}

export function formatCentralDateTime(date = new Date()) {
  return dayjs(date).tz(CENTRAL_TZ).format(CENTRAL_DATETIME_FORMAT);
}
